import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';

gsap.registerPlugin(Observer);

export function initFullPageScroll(containerSelector, options = {}) {
  const {
    sectionSelector = '.section',
    callbacks = {},
    globalKey = 'pageScroll',
    scrollableSectionIndexes = [],
    ignore = 'video, .about-video-wrap',
  } = options;

  const container = document.querySelector(containerSelector);
  if (!container || window.innerWidth < 1080) return null;

  const sections = gsap.utils.toArray(`${containerSelector} ${sectionSelector}`);
  if (sections.length <= 1) return null;

  let wrapper = container.querySelector('.page-scroll-wrapper');
  if (!wrapper) {
    wrapper = document.createElement('div');
    wrapper.className = 'page-scroll-wrapper';
    sections.forEach((section) => wrapper.appendChild(section));
    container.appendChild(wrapper);
  }

  let sectionOffsets = [];
  let currentIndex = 0;
  let canNavigate = true;
  let observer = null;
  const prevOverflow = document.body.style.overflow;
  const NAV_COOLDOWN = 400;

  document.body.style.overflow = 'hidden';

  const layoutSections = () => {
    let offset = 0;
    sectionOffsets = [];

    sections.forEach((section) => {
      const isAutoHeight = section.classList.contains('fp-auto-height');
      sectionOffsets.push(offset);

      gsap.set(section, {
        position: 'absolute',
        top: offset,
        left: 0,
        width: '100%',
        height: isAutoHeight ? 'auto' : window.innerHeight,
        minHeight: isAutoHeight ? window.innerHeight : undefined,
      });

      offset += isAutoHeight
        ? Math.max(section.offsetHeight, window.innerHeight)
        : window.innerHeight;
    });

    gsap.set(wrapper, { height: offset, y: -sectionOffsets[currentIndex] || 0 });
  };

  layoutSections();

  const scrollWithinSection = (direction) => {
    if (!scrollableSectionIndexes.includes(currentIndex)) return false;

    const section = sections[currentIndex];
    const maxScroll = section.scrollHeight - section.clientHeight;
    if (maxScroll <= 0) return false;

    const step = window.innerHeight * 0.6;
    if (direction > 0 && section.scrollTop < maxScroll - 1) {
      section.scrollTop = Math.min(section.scrollTop + step, maxScroll);
      return true;
    }
    if (direction < 0 && section.scrollTop > 0) {
      section.scrollTop = Math.max(section.scrollTop - step, 0);
      return true;
    }
    return false;
  };

  const gotoSection = (index) => {
    index = gsap.utils.clamp(0, sections.length - 1, index);
    if (!canNavigate || index === currentIndex) return;

    const prevIndex = currentIndex;
    currentIndex = index;
    canNavigate = false;

    sections.forEach((section, i) => {
      if (!scrollableSectionIndexes.includes(i)) section.scrollTop = 0;
    });

    if (prevIndex !== index) {
      callbacks.onLeave?.(prevIndex, index);
    }

    gsap.to(wrapper, {
      y: -sectionOffsets[index],
      duration: 0.85,
      ease: 'power2.inOut',
      onComplete: () => {
        callbacks.afterLoad?.(index);
        setTimeout(() => {
          canNavigate = true;
        }, NAV_COOLDOWN);
      },
    });
  };

  observer = Observer.create({
    target: container,
    type: 'wheel,touch',
    tolerance: 60,
    preventDefault: true,
    allowClicks: true,
    ignore,
    onDown: () => {
      if (scrollWithinSection(1)) return;
      gotoSection(currentIndex + 1);
    },
    onUp: () => {
      if (scrollWithinSection(-1)) return;
      gotoSection(currentIndex - 1);
    },
  });

  callbacks.afterLoad?.(0);

  const api = {
    scrollTo(index) {
      gsap.killTweensOf(wrapper);
      canNavigate = true;
      gotoSection(index);
    },
    destroy() {
      observer?.kill();
      gsap.killTweensOf(wrapper);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('resize', onResize);
      if (window[globalKey] === api) window[globalKey] = null;
    },
    refresh() {
      layoutSections();
    },
  };

  const onResize = () => {
    if (window.innerWidth < 1080) {
      api.destroy();
      return;
    }
    api.refresh();
  };
  window.addEventListener('resize', onResize);

  window[globalKey] = api;
  return api;
}
